import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { MY_BRAND, UPDATE_BRAND_SETTINGS } from '../../services/brandService/queries';
import { Button } from '../../components/button';
import SwitchComponent from '../../components/switch';
import { HostCapabilitiesSection } from './HostCapabilitiesSection';
import { CancellationRulesSection } from './CancellationRulesSection';
import { ServiceRulesSection } from './ServiceRulesSection';
import { brandSellsServices } from './serviceRulesAdmin';
import { CarSettingsSection } from './CarSettingsSection';
import { BookingsOpenSection } from './BookingsOpenSection';
import { RegionSection } from './RegionSection';
import { SectionNavLayout, SettingsSection } from '../../components/SectionNav';
import {
  BRAND_SECTION_ID,
  BRAND_SETTINGS_GROUPS,
  visibleBrandSettingsSections,
} from './brandSettingsSections';
import {
  Alert,
  Badge,
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
} from '../../components/ui';

const ToggleRow = styled.label`
  ${tw`flex items-start justify-between gap-4 py-3 cursor-pointer`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

const ToggleText = styled.div`
  ${tw`min-w-0`}
`;

const ToggleTitle = styled.div`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;

const ToggleHint = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;

const MarketRow = styled.div`
  ${tw`grid grid-cols-1 gap-3 p-4 sm:grid-cols-[1.4fr_1fr_1fr_1fr_auto] sm:items-end`}
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background-color: var(--color-bg-secondary);
`;

const MarketList = styled.div`
  ${tw`flex flex-col gap-3`}
`;

type Market = { latitude: string; longitude: string; label: string; radiusMiles: string };

/** Comma or newline separated domains → normalized list. */
function parseDomains(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map(v =>
      v
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/.*$/, ''),
    )
    .filter(Boolean);
}

/**
 * The viewing brand's settings (decision summary #12 to #15; D16, D19, D23, D29).
 * Lenvoy's settings change what lenvoy.co shows, never what autopilotee.com shows.
 */
export default function AdminBrandSettingsPage() {
  const { data, loading, error } = useQuery(MY_BRAND, { fetchPolicy: 'network-only' });
  const [updateBrandSettings, { loading: saving }] = useMutation(UPDATE_BRAND_SETTINGS, {
    refetchQueries: [{ query: MY_BRAND }],
  });

  const [displayName, setDisplayName] = useState('');
  const [referralRate, setReferralRate] = useState('');
  const [showOtherSites, setShowOtherSites] = useState(false);
  const [banAllOtherBrands, setBanAllOtherBrands] = useState(false);
  const [hideFromAllOtherBrands, setHideFromAllOtherBrands] = useState(false);
  const [banBrandSites, setBanBrandSites] = useState('');
  const [banMyselfOnOthers, setBanMyselfOnOthers] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [markets, setMarkets] = useState<Market[]>([]);

  useEffect(() => {
    const brand = data?.myBrand;
    if (!brand) return;
    setDisplayName(brand.displayName ?? '');
    setReferralRate(
      brand.referralCommissionRate === null || brand.referralCommissionRate === undefined
        ? ''
        : String(brand.referralCommissionRate),
    );
    setShowOtherSites(Boolean(brand.showOtherSites));
    setBanAllOtherBrands(Boolean(brand.banAllOtherBrands));
    setHideFromAllOtherBrands(Boolean(brand.hideFromAllOtherBrands));
    setBanBrandSites((brand.banBrandSites ?? []).join(', '));
    setBanMyselfOnOthers((brand.banMyselfOnOthers ?? []).join(', '));
    setFromEmail(brand.fromEmail ?? '');
    setSupportEmail(brand.supportEmail ?? '');
    setMarkets(
      (brand.exclusiveMarkets ?? []).map((m: any) => ({
        latitude: String(m.latitude),
        longitude: String(m.longitude),
        label: m.label ?? '',
        radiusMiles: m.radiusMiles ? String(m.radiusMiles) : '',
      })),
    );
  }, [data?.myBrand]);

  const updateMarket = (index: number, patch: Partial<Market>) =>
    setMarkets(ms => ms.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  const handleSave = async () => {
    const exclusiveMarkets = markets
      .filter(m => m.latitude.trim() && m.longitude.trim())
      .map(m => ({
        latitude: Number(m.latitude),
        longitude: Number(m.longitude),
        label: m.label.trim(),
        radiusMiles: m.radiusMiles.trim() ? Number(m.radiusMiles) : null,
      }));
    try {
      await updateBrandSettings({
        variables: {
          input: {
            displayName: displayName.trim() || null,
            referralCommissionRate: referralRate.trim() === '' ? null : Number(referralRate),
            showOtherSites,
            banAllOtherBrands,
            hideFromAllOtherBrands,
            banBrandSites: parseDomains(banBrandSites),
            banMyselfOnOthers: parseDomains(banMyselfOnOthers),
            exclusiveMarkets,
            fromEmail: fromEmail.trim() || null,
            supportEmail: supportEmail.trim() || null,
          },
        },
      });
      toast.success('Brand settings saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save brand settings');
    }
  };

  const brand = data?.myBrand;
  const saveButton = (
    <Button
      text={saving ? 'Saving…' : 'Save brand settings'}
      type="button"
      onClick={handleSave}
      isLoading={saving}
      isDisabled={loading || !!error}
    />
  );

  const brandName = brand?.displayName || brand?.brandSite || 'this brand';
  const sellsServices = brandSellsServices(brand?.allowedProductTypes);
  const navSections = useMemo(
    () => visibleBrandSettingsSections({ sellsServices }),
    [sellsServices],
  );

  return (
    <SectionNavLayout
      sections={navSections}
      groups={BRAND_SETTINGS_GROUPS}
      label="Brand settings sections"
      storageKey="brand_settings_nav_expanded_v1"
      ready={!loading && !error}
    >
      <PageContainer width="lg" canvas>
        <PageHeader
          eyebrow="Admin"
          title={
            <>
              Brand settings{' '}
              {brand?.brandSite && (
                <Badge tone="brand" size="md">
                  {brand.brandSite}
                </Badge>
              )}
            </>
          }
          description={`These settings change what guests on ${
            brand?.brandSite ?? 'this brand'
          } see. They do not change what other brands show.`}
          actions={saveButton}
        />

        {loading && <LoadingBlock label="Loading brand settings" />}
        {error && (
          <Alert tone="error" title="Could not load brand settings">
            {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <Stack>
            <SettingsSection id={BRAND_SECTION_ID.bookings} label="Bookings">
              <BookingsOpenSection brandName={brandName} />
            </SettingsSection>
            <SettingsSection id={BRAND_SECTION_ID.region} label="Region">
              <RegionSection brandName={brandName} />
            </SettingsSection>

            <SettingsSection id={BRAND_SECTION_ID.otherBrands} label="Other brands in search">
              <Card>
                <CardHeader
                  title="Other brands in search"
                  description="Off: only this brand's cars, including address and airport search. On: other brands' cars may appear in address and airport search unless banned here, hidden by that brand, or inside one of your exclusive markets. Homepage sections, featured cars and hub search stay on this brand."
                />
                <CardBody>
                  <ToggleRow>
                    <ToggleText>
                      <ToggleTitle>Show other brands&apos; cars</ToggleTitle>
                      <ToggleHint>In address and airport search on this site.</ToggleHint>
                    </ToggleText>
                    <SwitchComponent
                      isActive={showOtherSites}
                      onChange={e => setShowOtherSites(e.target.checked)}
                      inputTestId="brand-show-other-sites"
                      ariaLabel="Show other brands' cars"
                    />
                  </ToggleRow>
                  <ToggleRow>
                    <ToggleText>
                      <ToggleTitle>Ban every other brand on this site</ToggleTitle>
                      <ToggleHint>Ignores the list below.</ToggleHint>
                    </ToggleText>
                    <SwitchComponent
                      isActive={banAllOtherBrands}
                      onChange={e => setBanAllOtherBrands(e.target.checked)}
                      ariaLabel="Ban every other brand on this site"
                    />
                  </ToggleRow>
                  <Box $mt={4}>
                    <Field
                      label="Banned brands on this site"
                      htmlFor="brand-ban-sites"
                      hint="Domains only. These brands never appear here, even when the switch above is on."
                    >
                      <Input
                        id="brand-ban-sites"
                        placeholder="autopilotee.com, other.com"
                        value={banBrandSites}
                        onChange={e => setBanBrandSites(e.target.value)}
                        disabled={banAllOtherBrands}
                      />
                    </Field>
                  </Box>
                </CardBody>
              </Card>
            </SettingsSection>

            <SettingsSection id={BRAND_SECTION_ID.appearIn} label="Where this brand may appear">
              <Card>
                <CardHeader
                  title="Where this brand may appear"
                  description="Other brands can show your cars only when they turn their own switch on. You can still refuse: list the brands that must not show your cars, or hide from every other brand."
                />
                <CardBody>
                  <ToggleRow>
                    <ToggleText>
                      <ToggleTitle>Hide this brand&apos;s cars from every other brand</ToggleTitle>
                      <ToggleHint>Ignores the list below.</ToggleHint>
                    </ToggleText>
                    <SwitchComponent
                      isActive={hideFromAllOtherBrands}
                      onChange={e => setHideFromAllOtherBrands(e.target.checked)}
                      ariaLabel="Hide this brand's cars from every other brand"
                    />
                  </ToggleRow>
                  <Box $mt={4}>
                    <Field
                      label="Brands that must not show our cars"
                      htmlFor="brand-ban-myself"
                      hint="Domains only. Either side's ban wins."
                    >
                      <Input
                        id="brand-ban-myself"
                        placeholder="autopilotee.com"
                        value={banMyselfOnOthers}
                        onChange={e => setBanMyselfOnOthers(e.target.value)}
                        disabled={hideFromAllOtherBrands}
                      />
                    </Field>
                  </Box>
                </CardBody>
              </Card>
            </SettingsSection>

            <SettingsSection id={BRAND_SECTION_ID.exclusiveMarkets} label="Exclusive markets">
              <Card>
                <CardHeader
                  title="Exclusive markets"
                  description="Inside any circle only this brand's cars are shown, and another brand's car located inside a circle is never shown. The radius defaults to 150 miles."
                  actions={
                    <Button
                      text="Add market"
                      theme="secondary"
                      size="sm"
                      type="button"
                      onClick={() =>
                        setMarkets(ms => [
                          ...ms,
                          { latitude: '', longitude: '', label: '', radiusMiles: '' },
                        ])
                      }
                    />
                  }
                />
                {markets.length === 0 ? (
                  <EmptyState
                    compact
                    icon={faMapLocationDot}
                    title="No exclusive markets"
                    description="Add a circle to keep a city to this brand's cars."
                  />
                ) : (
                  <CardBody>
                    <MarketList>
                      {markets.map((market, index) => (
                        <MarketRow key={index}>
                          <Field label="Label">
                            <Input
                              placeholder="Bay Area"
                              value={market.label}
                              onChange={e => updateMarket(index, { label: e.target.value })}
                            />
                          </Field>
                          <Field label="Latitude">
                            <Input
                              type="number"
                              placeholder="37.77"
                              value={market.latitude}
                              onChange={e => updateMarket(index, { latitude: e.target.value })}
                            />
                          </Field>
                          <Field label="Longitude">
                            <Input
                              type="number"
                              placeholder="-122.41"
                              value={market.longitude}
                              onChange={e => updateMarket(index, { longitude: e.target.value })}
                            />
                          </Field>
                          <Field label="Radius (mi)">
                            <Input
                              type="number"
                              placeholder="150"
                              value={market.radiusMiles}
                              onChange={e => updateMarket(index, { radiusMiles: e.target.value })}
                            />
                          </Field>
                          <Button
                            text="Remove"
                            theme="text"
                            size="sm"
                            type="button"
                            onClick={() => setMarkets(ms => ms.filter((_, i) => i !== index))}
                          />
                        </MarketRow>
                      ))}
                    </MarketList>
                  </CardBody>
                )}
              </Card>
            </SettingsSection>

            <SettingsSection id={BRAND_SECTION_ID.referral} label="Referral fee and emails">
              <Card>
                <CardHeader
                  title="Referral fee and emails"
                  description="When a guest is redirected here from another brand and pays, this brand owes that brand this percent of the car price. Leave empty to use the platform default. The platform sets a minimum you cannot go below."
                />
                <CardBody>
                  <FormGrid>
                    <Field label="Referral percent this brand pays" htmlFor="brand-referral-rate">
                      <Input
                        id="brand-referral-rate"
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        placeholder="Platform default"
                        value={referralRate}
                        onChange={e => setReferralRate(e.target.value)}
                      />
                    </Field>
                    <Field label="Brand display name" htmlFor="brand-display-name">
                      <Input
                        id="brand-display-name"
                        value={displayName}
                        onChange={e => setDisplayName(e.target.value)}
                      />
                    </Field>
                    <Field label="From address for this brand's emails" htmlFor="brand-from-email">
                      <Input
                        id="brand-from-email"
                        type="email"
                        placeholder="hello@lenvoy.co"
                        value={fromEmail}
                        onChange={e => setFromEmail(e.target.value)}
                      />
                    </Field>
                    <Field label="Support address shown to hosts" htmlFor="brand-support-email">
                      <Input
                        id="brand-support-email"
                        type="email"
                        placeholder="support@lenvoy.co"
                        value={supportEmail}
                        onChange={e => setSupportEmail(e.target.value)}
                      />
                    </Field>
                  </FormGrid>
                </CardBody>
                <CardFooter>{saveButton}</CardFooter>
              </Card>
            </SettingsSection>

            <SettingsSection id={BRAND_SECTION_ID.cancellation} label="Cancellation rules">
              <CancellationRulesSection brandName={brandName} />
            </SettingsSection>

            {sellsServices && (
              <SettingsSection id={BRAND_SECTION_ID.serviceRules} label="Service rules">
                <ServiceRulesSection brandName={brandName} />
              </SettingsSection>
            )}

            <CarSettingsSection
              brandName={brandName}
              carSettingsId={BRAND_SECTION_ID.carSettings}
              bookingRulesId={BRAND_SECTION_ID.bookingRules}
            />

            <HostCapabilitiesSection
              brandName={brandName}
              permissionsId={BRAND_SECTION_ID.hostPermissions}
              historyId={BRAND_SECTION_ID.history}
            />
          </Stack>
        )}
      </PageContainer>
    </SectionNavLayout>
  );
}
